import { FC, useMemo } from "react";

import { copy } from "shared/assets";
import { Plus } from "shared/assets/icons";
import { FormBuilder } from "shared/components";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { IStage, IStageCapacity } from "Venues/types";
import { capacityTypeOptions } from "Venues/utils";
import { getFields } from "./formData";

const stageCapacityCopy = copy.venueBlock.stageCapacity;

interface StageCapacityFormProps {
  stages?: IStage[];
  capacities?: IStageCapacity[];
  defaultValues?: Record<string, any>;
  handleAddStage?: () => void;
  handleAddCapacity?: () => void;
  handleChangeStage?: (stageId: string) => void;
  handleChangeCapacity?: (capacityId: string) => void;
  showCapacityField?: boolean;
}

export const StageCapacityForm: FC<StageCapacityFormProps> = ({
  stages = [],
  capacities = [],
  defaultValues,
  handleAddStage,
  handleAddCapacity,
  handleChangeStage,
  handleChangeCapacity,
  showCapacityField,
}) => {
  const stagesOrAction = useMemo(() => {
    let addAction: ISelectOption | null = null;

    if (handleAddStage) {
      addAction = {
        label: stageCapacityCopy.buttons.addStage,
        value: "",
        icon: Plus,
        onClick: handleAddStage,
      };
    }

    const stageOptions = stages.map(({ id, title, stageCapacities }) => ({
      value: id,
      label: title,
      suffix: stageCapacities?.length ? `(${stageCapacities.length} capacities)` : undefined,
    }));

    return addAction ? [...stageOptions, addAction] : stageOptions;
  }, [handleAddStage, stages]);

  const capacititesOrAction = useMemo(() => {
    let addAction: ISelectOption | null = null;

    if (handleAddCapacity) {
      addAction = {
        label: stageCapacityCopy.buttons.addCapacity,
        value: "",
        icon: Plus,
        onClick: handleAddCapacity,
      };
    }

    const capacityOptions = capacities.map(({ id, title, capacity, type }) => ({
      value: id,
      label: title,
      suffix: `(${capacity} × ${capacityTypeOptions[type]})`,
    }));

    return addAction ? [...capacityOptions, addAction] : capacityOptions;
  }, [handleAddCapacity, capacities]);

  const fields = useMemo(
    () =>
      getFields({
        stages: stagesOrAction,
        capcities: capacititesOrAction,
        handleChangeStage,
        handleChangeCapacity,
        showCapacityField,
      }),
    [
      stagesOrAction,
      capacititesOrAction,
      handleChangeStage,
      handleChangeCapacity,
      showCapacityField,
    ]
  );

  const onSubmit = () => {
    // do nothing
  };

  if (!stagesOrAction.length) return null;

  return (
    <FormBuilder
      fields={fields}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      forceUpdateValues={defaultValues}
      style={{
        gridTemplateColumns: "1fr 1fr",
        mobile: {
          gridTemplateColumns: "1fr",
        },
      }}
    />
  );
};
